import React, { useState, useEffect } from 'react';
import Form from '../Form';
import Modal from '../Modal';
import { useAuth } from '../../helpers/AuthContext';
import './ProductBulkEditor.css';
import taxonomy from 'mayhem-taxonomy';

export default function ProductBulkEditor({
    selectedProducts,
    allProducts,
    onClose,
    onSave,
    channel
}) {
    const { fetch } = useAuth();
    const [formValues, setFormValues] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedProductsData = allProducts.filter(product =>
        selectedProducts.includes(product.uuid)
    );

    const categories = channel?.isRed ? taxonomy.categories_red : taxonomy.categories;
    const madeByOptions = [
        { value: 'seller', label: 'Yes, I designed and created this' },
        { value: 'hand-assembled', label: 'I hand-assemble these from purchased parts I modify' },
        { value: 'licensed', label: 'I produce this, but I don\'t own the design - it\'s licensed' },
        { value: 'vintage', label: 'This is a vintage item' },
        { value: 'collectable', label: 'This is a collectable' },
        { value: 'resale', label: 'I\'m reselling this item' },
        { value: 'pod', label: 'Print-on-demand, but my unique design' },
        { value: 'designer', label: "I designed this, but someone else produced it" }
    ];

    const fields = [
        {
            name: 'isAvailable',
            type: 'select',
            label: 'For Sale?',
            options: [
                { value: '', label: 'No Change' },
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' }
            ],
            help: 'Set whether selected products should be available for sale'
        },
        {
            name: 'category',
            type: 'select',
            label: 'Primary Category',
            options: [
                { value: '', label: 'No Change' },
                ...categories
            ],
            help: 'Change the primary category for selected products'
        },
        ...(channel?.isRed === false ? [{
            name: 'madeBy',
            type: 'select',
            label: 'Made By',
            options: [
                { value: '', label: 'No Change' },
                ...madeByOptions
            ],
            help: 'Change who made the selected products'
        }] : []),
        {
            name: 'inMarketplace',
            type: 'select',
            label: 'List in Marketplace?',
            options: [
                { value: '', label: 'No Change' },
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' }
            ],
            help: 'Set whether selected products should appear in the marketplace'
        },
        ...(channel?.isRed === false ? [{
            name: 'isBarterable',
            type: 'select',
            label: 'Allow Barter / Trades',
            options: [
                { value: '', label: 'No Change' },
                { value: 'true', label: 'Yes, allow people to offer me trades' },
                { value: 'false', label: 'No, accepting payment only' }
            ],
            help: 'Set whether selected products can be bartered/traded'
        }] : [])
    ];

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            // Filter out empty values (no change)
            const updates = Object.entries(formValues)
                .filter(([_, value]) => value !== '')
                .reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

            if (Object.keys(updates).length === 0) {
                onClose();
                return;
            }

            const response = await fetch('/account/products/bulk-update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    productIds: selectedProducts,
                    updates
                })
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                const message = `Successfully updated ${selectedProducts.length} products`;
                onSave(data.warning ? `${message}. ${data.warning}` : message);
                onClose();
            } else {
                throw new Error('Failed to update products');
            }
        } catch (error) {
            console.error('Error updating products:', error);
            onSave('', 'Failed to update products. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const modalFooter = (
        <div className="bulk-editor-actions">
            <button
                type="button"
                className="button secondary"
                onClick={onClose}
                disabled={isSubmitting}
            >
                Cancel
            </button>
            <button
                type="submit"
                className="button primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Updating...' : 'Update Products'}
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={`Bulk Edit ${selectedProducts.length} Products`}
            size="medium"
            footer={modalFooter}
            closeOnOverlayClick={!isSubmitting}
            ariaLabelledBy="bulk-editor-title"
            ariaDescribedBy="bulk-editor-content"
        >
            <div className="bulk-editor-content" id="bulk-editor-content">
                <div className="selected-products">
                    <h4>Selected Products:</h4>
                    <ul>
                        {selectedProductsData.map(product => (
                            <li key={product.uuid}>
                                {product.name} ({product.type})
                            </li>
                        ))}
                    </ul>
                </div>

                <Form
                    id="bulk-editor-form"
                    fields={fields}
                    values={formValues}
                    onChange={setFormValues}
                    onSubmit={handleSubmit}
                    className="bulk-editor-form"
                />
            </div>
        </Modal>
    );
}
